<script lang="ts" setup generic="TValues extends object = Record<string, unknown>, TName extends string = string">
import type { VNodeChild } from 'vue';
import type { FormSchemaDataField, FormSchemaFieldValue } from '#layers/block-engine/types/form-schema';
import type { FormSchemaAdapter } from '#layers/block-engine/utils/form-schema/adapter';
import { createReusableTemplate } from '@vueuse/core';
import { computed } from 'vue';
import { blockExpressionMarker } from '#layers/block-engine/utils/block/expression';
import { provideFieldNullable } from '#layers/forms/public';
import { useTranslationUtils } from '#layers/i18n/public';
import { injectFormContext } from '#layers/ui/public';
import { RenderFnWrapper } from './RenderFnWrapper';

const {
	field,
	adapter,
} = defineProps<{
	field: FormSchemaDataField<TValues, TName>;
	adapter: FormSchemaAdapter<TValues>;
}>();

const emit = defineEmits<{
	setValue: [value: FormSchemaFieldValue<TValues, TName>];
}>();

const { translateString } = useTranslationUtils();

// Let input blocks derive a clear affordance from the field's nullability.
provideFieldNullable(() => field.nullable ?? false);

const model = computed<FormSchemaFieldValue<TValues, TName>>({
	get: () => {
		// Defaults only apply after the active adapter source reports an unset value.
		const value = adapter.get({
			field,
		});
		const defaultOnNull = 'defaultOnNull' in field && field.defaultOnNull;
		const defaultValue = 'default' in field ? field.default : undefined;
		// Missing values fall back to schema defaults; an explicit null is preserved
		// (the user cleared the field) unless the field opts into defaultOnNull.
		const useDefault = value === undefined || (value === null && defaultOnNull);
		return (useDefault ? defaultValue?.() : value) as FormSchemaFieldValue<TValues, TName>;
	},
	set: (value) => {
		emit('setValue', value);
	},
});

/**
 * Update the local model from custom render slots.
 */
function updateModel(value: FormSchemaFieldValue<TValues, TName>) {
	model.value = value;
}

// Render

const render = computed(() => {
	if ('render' in field && field.render) {
		return field.render;
	}

	return false;
});

// Block slots

const fieldSlots = computed<Record<string, (slotProps: Record<string, unknown>) => VNodeChild>>(() => {
	if ('slots' in field && field.slots) {
		return field.slots as Record<string, (slotProps: Record<string, unknown>) => VNodeChild>;
	}

	return {};
});

const block = computed(() => 'block' in field ? field.block : undefined);
const blockProps = computed(() => 'props' in field ? field.props : undefined);
const fieldProps = computed(() => 'fieldProps' in field ? field.fieldProps : undefined);
const fieldIcon = computed(() => 'icon' in field ? field.icon : undefined);
const fieldRequired = computed(() => 'required' in field ? field.required : undefined);

// Special handling for InputSwitch block

const formContext = injectFormContext();

const isStackedLayout = computed(() => formContext.layout.value === 'stacked' || fieldProps.value?.layout === 'stacked');

const isExpression = computed(() => {
	const value = model.value;
	return typeof value === 'string' && value.startsWith(blockExpressionMarker);
});

const isInputSwitch = computed(() => block.value === '$builtin:InputSwitch');

const moveSwitchToLabel = computed(() => isStackedLayout.value && !isExpression.value && isInputSwitch.value && !render.value);

// Reusable template

const [DefineRenderContent, RenderContent] = createReusableTemplate();
</script>

<template>
	<DefineRenderContent>
		<slot
			v-if="render"
			name="render"
			:render
			:value="model"
			:set-value="updateModel"
		>
			<RenderFnWrapper
				v-model="model"
				:render-fn="render"
				class="w-full"
			/>
		</slot>

		<BlockRender
			v-else-if="!moveSwitchToLabel && block"
			v-model="model"
			:block
			:props="blockProps"
		>
			<template
				v-for="(slot, name) in fieldSlots"
				:key="name"
				#[name]="slotProps"
			>
				<RenderFnWrapper :render-fn="() => slot(slotProps)" />
			</template>
		</BlockRender>
	</DefineRenderContent>

	<RenderContent
		v-if="field.unwrapField"
		v-bind="$attrs"
	/>
	<DFormField
		v-else
		v-bind="{
			...$attrs,
			...fieldProps,
		}"
		:data-field-name="field.name"
		:name="field.name"
		:description="translateString(field.description)"
		:required="fieldRequired"
		:ui="{
			label: 'flex w-full items-center',
		}"
		class="group/form-schema-field"
		:class="{
			'[&_label+div]:hidden': moveSwitchToLabel,
		}"
	>
		<template #label>
			<div
				v-if="moveSwitchToLabel"
			>
				<DSwitch
					v-model="model"
					class="me-2"
				/>
			</div>

			<DIconOrImage
				v-if="fieldIcon"
				:icon="fieldIcon"
			/>

			<DFormFieldLabel :label="translateString(field.label)" :required="fieldRequired" />

			<!-- Prevent switch toggle -->
			<div
				class="flex flex-1 items-center"
				@click.prevent
			>
				<slot name="hint" />
			</div>
		</template>

		<slot>
			<RenderContent />
		</slot>
	</DFormField>
</template>
